import React, { useId } from 'react';
import type { PaginationProps } from './types';

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_PAGE_SIZE_OPTIONS = [20, 50, 100];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Builds the page number sequence shown in the control.
 * Always includes first, last, current ±1, and ellipsis gaps.
 * Returns numbers or null (= ellipsis).
 */
function buildPages(current: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | null)[] = [];
  const addPage = (n: number | null) => {
    if (pages[pages.length - 1] !== n) pages.push(n);
  };

  addPage(1);
  if (current > 3) addPage(null); // left ellipsis

  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
    addPage(p);
  }

  if (current < total - 2) addPage(null); // right ellipsis
  addPage(total);

  return pages;
}

// ─── Icon buttons ─────────────────────────────────────────────────────────────

function ChevronLeft() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// ─── Shared button classes ────────────────────────────────────────────────────

const BTN_BASE =
  'inline-flex items-center justify-center h-8 min-w-[32px] px-1 rounded text-sm ' +
  'border transition-colors duration-150 select-none ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1';

const BTN_DEFAULT =
  'border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 ' +
  'bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-700';

const BTN_ACTIVE =
  'border-brand-500 bg-brand-500 text-white font-semibold pointer-events-none';

const BTN_DISABLED =
  'border-neutral-200 dark:border-neutral-700 text-neutral-300 dark:text-neutral-600 ' +
  'bg-white dark:bg-neutral-800 cursor-not-allowed pointer-events-none';

// ─── Pagination ───────────────────────────────────────────────────────────────

export function Pagination({
  currentPage = 1,
  totalPages,
  totalItems,
  pageSize = 20,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageChange,
  onPageSizeChange,
  showItemCount = true,
  loading = false,
}: PaginationProps) {
  const selectId = useId();
  const liveId = useId();

  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages;
  const isDisabled = loading;

  // Clamp for display
  const fromItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const toItem = Math.min(currentPage * pageSize, totalItems);

  const pages = buildPages(currentPage, totalPages);

  function go(page: number) {
    if (isDisabled || page < 1 || page > totalPages || page === currentPage) return;
    onPageChange?.(page);
  }

  return (
    <nav
      aria-label="Pagination"
      className="flex flex-wrap items-center justify-between gap-3 text-sm"
    >
      {/* Live region — announces page changes to screen readers */}
      <div
        id={liveId}
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {`Page ${currentPage} of ${totalPages}`}
      </div>

      {/* Left: item count + page-size selector (hidden on mobile) */}
      <div className="hidden sm:flex items-center gap-3 text-neutral-500 dark:text-neutral-400">
        {showItemCount && totalItems > 0 && (
          <span>
            Showing{' '}
            <span className="font-medium text-neutral-700 dark:text-neutral-200">
              {fromItem}–{toItem}
            </span>{' '}
            of{' '}
            <span className="font-medium text-neutral-700 dark:text-neutral-200">
              {totalItems.toLocaleString('en-IN')}
            </span>
          </span>
        )}

        {onPageSizeChange && (
          <label className="flex items-center gap-1.5">
            <span className="text-xs">Rows</span>
            <select
              id={selectId}
              value={pageSize}
              disabled={isDisabled}
              aria-label="Items per page"
              className="h-7 rounded border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 text-xs px-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* Right: prev / page numbers / next */}
      <div className="flex items-center gap-1">
        {/* Prev */}
        <button
          type="button"
          className={[BTN_BASE, isFirst || isDisabled ? BTN_DISABLED : BTN_DEFAULT].join(' ')}
          onClick={() => go(currentPage - 1)}
          disabled={isFirst || isDisabled}
          aria-label="Previous page"
        >
          <ChevronLeft />
        </button>

        {/* Page numbers — hidden on mobile, show only current */}
        <>
          {/* Mobile: current page indicator */}
          <span className="flex sm:hidden items-center px-2 text-sm text-neutral-700 dark:text-neutral-200">
            {currentPage} / {totalPages}
          </span>

          {/* Desktop: full page sequence */}
          <div className="hidden sm:flex items-center gap-1">
            {pages.map((page, i) =>
              page === null ? (
                <span
                  key={`ellipsis-${i}`}
                  className="inline-flex items-center justify-center h-8 w-8 text-neutral-400 dark:text-neutral-500 select-none"
                  aria-hidden="true"
                >
                  &hellip;
                </span>
              ) : (
                <button
                  key={page}
                  type="button"
                  className={[
                    BTN_BASE,
                    'w-8',
                    isDisabled
                      ? BTN_DISABLED
                      : page === currentPage
                        ? BTN_ACTIVE
                        : BTN_DEFAULT,
                  ].join(' ')}
                  onClick={() => go(page)}
                  disabled={isDisabled}
                  aria-label={`Page ${page}`}
                  aria-current={page === currentPage ? 'page' : undefined}
                >
                  {page}
                </button>
              ),
            )}
          </div>
        </>

        {/* Next */}
        <button
          type="button"
          className={[BTN_BASE, isLast || isDisabled ? BTN_DISABLED : BTN_DEFAULT].join(' ')}
          onClick={() => go(currentPage + 1)}
          disabled={isLast || isDisabled}
          aria-label="Next page"
        >
          <ChevronRight />
        </button>
      </div>
    </nav>
  );
}
